// The map marks of a card head (mockup round 083 card 08): one 20 px mark
// per kind of map object, in the ink of its owner. The geometry is the
// mockup's; every mark paints in `currentColor`, so the head sets the ink
// once and the mark follows it, and no colour stands in the markup.

import type { ShipClass } from '../../gen/proto/aquaphobia';
import { DEFAULT_SHIP_STYLE, hullPathOf, shipStyleOf } from '../../render/hull-styles';
import { drawCP } from '../../render/marks';
import type { SceneLook } from '../../render/scene-look';
import {
  STRUCTURE_KINDS, drawStructureMark, isStructureSymbol, structureKindOf,
  type StructureInk, type StructureSymbolId,
} from '../../render/structures';
import { SvgPathMarks } from '../../render/svg-marks';
import { factionColor } from '../../world';
import { MONSTER, NPC_PATHS, NPC_STROKE, SWALLOWTAIL_EYE } from '../../render/npc-marks';
import { ICONS } from '../viewpanel';

/** The kinds of mark a card head draws. */
export type MarkKind = 'fleet' | 'contact' | 'hold' | 'inst' | 'object' | 'deposit' | 'sonar' | 'ridge' | 'heat'
  | 'vent' | 'ruin' | 'gate' | 'lair' | 'pirate' | 'monster' | 'wreck' | 'wreckcargo' | 'cache'
  | 'seamine' | 'deployable' | 'lane';

const NPC_EDGE = 'var(--npc-edge)';
/** The deep body of the inverse hull (R2874). */
const NPC_DEEP = 'rgb(var(--deep-rgb))';

const PATHS: Readonly<Record<MarkKind, string>> = {
  fleet: '<path d="M20 12L5 5.5l3.4 6.5L5 18.5z" fill="currentColor"/>',
  contact: '<path d="M20 12L5 5.5l3.4 6.5L5 18.5z" fill="none" stroke="currentColor"'
    + ' stroke-width="1.6" stroke-linejoin="round"/>',
  hold: '<path d="M3 16.5l4.5-3h9l4.5 3-4.5 3h-9z" fill="currentColor" fill-opacity=".35"'
    + ' stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>'
    + '<path d="M12 16V7" stroke="currentColor" stroke-width="1.6"/>'
    + '<path d="M12 3l3.2 5.2H8.8z" fill="currentColor"/>',
  inst: '<path d="M12 20V6" stroke="currentColor" stroke-width="1.6"/>'
    + '<rect x="12" y="4" width="6.5" height="5" fill="currentColor"/>'
    + '<path d="M7.5 21l4.5-3.5 4.5 3.5z" fill="currentColor"/>',
  object: '<path d="M12 2.5l8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5z" fill="none" stroke="currentColor"'
    + ' stroke-width="1.4" stroke-dasharray="2.6 1.8"/>'
    + '<circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="1.4"/>'
    + '<path d="M12 6.8v2.4M12 14.8v2.4M6.8 12h2.4M14.8 12h2.4" stroke="currentColor" stroke-width="1.4"/>',
  deposit: '<path d="M8 7l4.5 5L8 17l-4.5-5z" fill="currentColor"/>'
    + '<path d="M16 7l4.5 5-4.5 5-4.5-5z" fill="currentColor" fill-opacity=".55"/>',
  sonar: '<circle cx="12" cy="12" r="2" fill="currentColor"/>'
    + '<path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4M4.9 4.9a10 10 0 0 0 0 14.2'
    + 'M19.1 4.9a10 10 0 0 1 0 14.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  // A ridge seen from the sea (round 086 card 06 b).
  ridge: '<path d="M3 18h18M4 18l4-7 3 3 4-8 5 12" fill="none" stroke="currentColor" stroke-width="1.5"'
    + ' stroke-linejoin="round"/>',
  // The heat icon of the VIEW panel (round 086 card 12), in the hue of the
  // mode that paints.
  heat: `<g fill="none" stroke="currentColor" stroke-width="1.6">${ICONS.heat}</g>`,
  // The points of interest of v2 phase 3 (round 088 card 11, R2626): one
  // shape a kind, so greyscale reads.
  vent: '<path d="M4 20l8-8 8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>'
    + '<path d="M8 10q3-3 0-6M12 9q3-3 0-6M16 10q3-3 0-6" fill="none" stroke="currentColor" stroke-width="1.4"'
    + ' stroke-linecap="round"/>',
  ruin: '<rect x="4" y="6.5" width="16" height="11" fill="none" stroke="currentColor" stroke-width="1.5"/>'
    + '<path d="M4 12h16M10 6.5V12M14 12v5.5" stroke="currentColor" stroke-width="1.5"/>',
  gate: '<path d="M4 5v14M20 5v14M8 12h8M13 8.5l3.5 3.5-3.5 3.5" fill="none" stroke="currentColor"'
    + ' stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
  lair: '<circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2.5 2.5"/>'
    + '<circle cx="12" cy="12" r="3" fill="currentColor"/>',
  // The two marks of the NPC side (round 088 card 03 a, R2637): the one
  // geometry of `render/npc-marks.ts`, which the map and the legend draw,
  // in the ink of the head with the dark edge `--npc-edge`. The swallowtail
  // is the inverse hull (R2874): the deep body, the rim and the eye in the
  // ink of the head. The ring of a monster is a light line round a dark
  // core already.
  pirate: `<path d="${NPC_PATHS.swallowtail}" fill="${NPC_DEEP}" stroke="currentColor"`
    + ` stroke-width="${NPC_STROKE.rim}" stroke-linejoin="round"/>`
    + `<circle cx="${SWALLOWTAIL_EYE.x}" cy="${SWALLOWTAIL_EYE.y}" r="${SWALLOWTAIL_EYE.r}" fill="currentColor"/>`,
  monster: `<path d="${NPC_PATHS.arms}" fill="none" stroke="currentColor" stroke-width="${NPC_STROKE.line}" stroke-linecap="round"/>`
    + `<path d="${NPC_PATHS.ring}" fill="${NPC_EDGE}" stroke="currentColor" stroke-width="${NPC_STROKE.ring}"/>`
    + `<circle cx="${MONSTER.eye.x}" cy="${MONSTER.eye.y}" r="${MONSTER.eye.r}" fill="currentColor"/>`,
  // A wreck, the INT 1 symbol of card 08 (round 088 card 11): a keel and
  // three masts.
  wreck: '<path d="M2.5 12h19M7 7v10M12 6v12M17 7v10" fill="none" stroke="currentColor" stroke-width="1.6"'
    + ' stroke-linecap="round"/>',
  // A wreck the viewer holds a cargo sighting of (round 132 card 01, R2847):
  // the wreck moved left, and the cargo bale with its two cords under the
  // stern.
  wreckcargo: '<path d="M2 11h14M5 7v8M9 6v10M13 7v8" fill="none" stroke="currentColor" stroke-width="1.6"'
    + ' stroke-linecap="round"/>'
    + '<rect x="15.5" y="13.5" width="6.5" height="6.5" rx=".6" fill="currentColor" fill-opacity=".3"'
    + ' stroke="currentColor" stroke-width="1.4"/>'
    + '<path d="M18.75 13.5v6.5M15.5 16.75h6.5" stroke="currentColor" stroke-width="1.1"/>',
  // A cache (round 088 card 10, R2649): the cross of a marked spot in a
  // dotted ring.
  cache: '<path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>'
    + '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.2" stroke-dasharray="1.5 2.5"/>',
  // The sea objects of v2 phase 6 (round 088 card 13, R2658): the two row
  // icons of the DEPOT pill, a horned ball and a buoy on its anchor line.
  seamine: '<circle cx="12" cy="13.5" r="5" fill="currentColor" fill-opacity=".35" stroke="currentColor" stroke-width="1.6"/>'
    + '<path d="M12 8.5V4.5M7.6 10.2L5 7.6M16.4 10.2L19 7.6" fill="none" stroke="currentColor" stroke-width="1.6"'
    + ' stroke-linecap="round"/>',
  deployable: '<circle cx="12" cy="7.5" r="3" fill="currentColor" fill-opacity=".35" stroke="currentColor" stroke-width="1.6"/>'
    + '<path d="M12 10.5v6M9 16.5h6M4 20h16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  // A shipping lane (round 088 card 09, R2658): the dashed track in its
  // band, as chart card 16 draws it.
  lane: '<path d="M3 17L21 7" fill="none" stroke="currentColor" stroke-width="7" stroke-opacity=".18" stroke-linecap="round"/>'
    + '<path d="M3 17L21 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="3 2.5"/>',
};

/**
 * One mark at 20 px. The ink is a CSS colour the mark takes as its
 * `color`; a hull mark points up, the way the map draws a hull at rest.
 */
export function markSvg(kind: MarkKind, ink: string): string {
  const turn = kind === 'fleet' || kind === 'contact' || kind === 'pirate' ? ' transform="rotate(-90 12 12)"' : '';
  return `<svg class="ck-mark" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"`
    + ` style="color:${ink}"><g${turn}>${PATHS[kind]}</g></svg>`;
}

const shipMarks = new Map<string, string>();

/**
 * The hull mark of a class (R1844): the silhouette the map and the dock
 * draw for that class, in the ship icon set of the reader's look (R835),
 * as SVG in `currentColor`. It points up, the way the map draws a hull at
 * rest. The fleet row of FLEET · MISSIONS draws it beside the class word,
 * and a group slot beside its digit (R1859), so one hull wears one mark
 * on the glass. No look is the AUTO set.
 */
export function shipMarkSvg(cls: ShipClass, look?: SceneLook): string {
  const style = look ? shipStyleOf(look, cls) : DEFAULT_SHIP_STYLE;
  const key = `${style}:${cls}`;
  const hit = shipMarks.get(key);
  if (hit !== undefined) return hit;
  const g = new SvgPathMarks();
  hullPathOf(style, cls)(g);
  g.fill({ color: 0xffffff });
  const svg = '<svg class="ck-ship" viewBox="-12 -18 24 36" aria-hidden="true">'
    + `<g transform="rotate(-90)">${g.markup()}</g></svg>`;
  shipMarks.set(key, svg);
  return svg;
}

/**
 * The ink of one strategic object's mark, as the map bakes it (the atlas key
 * `site:cp:<set>:<kind>:<faction>:<attacker>`, R1104): the holder's colour or
 * the neutral grey, and the attacker's colour while a pledge stands.
 */
export function structureInk(owner: number, contested: boolean, attacker: number): StructureInk {
  return {
    own: factionColor(owner),
    foe: contested && attacker !== 0 ? factionColor(attacker) : 0,
    held: owner !== 0,
  };
}

/** The structure symbol set of a look; no look is the AUTO set. */
function structureSetOf(look?: SceneLook): StructureSymbolId {
  const set = look?.structure;
  return set !== undefined && isStructureSymbol(set) ? set : 'default';
}

/** The inks the box of a set is measured in: still, contested and neutral. */
const BOX_INKS: readonly StructureInk[] = [
  { own: 1, foe: 0, held: true }, { own: 1, foe: 1, held: true }, { own: 1, foe: 0, held: false },
];

const structureBoxes = new Map<StructureSymbolId, number>();

/**
 * The half side of the box of one set: the reach of its widest kind, still
 * or contested. One box for all nine kinds keeps the size of each kind
 * against the others as the map draws it (the Throne is the larger mark).
 */
function structureBox(set: StructureSymbolId): number {
  const hit = structureBoxes.get(set);
  if (hit !== undefined) return hit;
  let r = 0;
  for (const kind of STRUCTURE_KINDS) {
    for (const ink of BOX_INKS) {
      const g = new SvgPathMarks();
      drawStructureMark(g, set, kind, ink);
      r = Math.max(r, g.reach());
    }
  }
  r = Math.ceil(r);
  structureBoxes.set(set, r);
  return r;
}

const structureMarks = new Map<string, string>();

/**
 * The map glyph of one strategic object (R1987): the structure symbol that
 * the map draws for its kind, in the structure symbol set of the reader's
 * look (R1103), in its own inks, as SVG. The site atlas bakes the same
 * description, so the icon and the map read one source. The live halo of
 * the `default` set is the site layer's and stays on the map (R1104). An
 * `ObjKind` the build does not draw takes the unkeyed mark, as on the map.
 * WAR · STRUCTURES draws it as the icon of an object row.
 */
export function structureMarkSvg(objKind: number, ink: StructureInk, look?: SceneLook): string {
  const set = structureSetOf(look);
  const key = `${set}:${objKind}:${ink.own}:${ink.foe}:${ink.held ? 1 : 0}`;
  const hit = structureMarks.get(key);
  if (hit !== undefined) return hit;
  const g = new SvgPathMarks({ colors: true });
  const kind = structureKindOf(objKind);
  if (kind === null) drawCP(g, ink.own);
  else drawStructureMark(g, set, kind, ink);
  const r = kind === null ? Math.ceil(g.reach()) : structureBox(set);
  const svg = `<svg class="ck-obj" viewBox="${-r} ${-r} ${2 * r} ${2 * r}" aria-hidden="true">`
    + `${g.markup()}</svg>`;
  structureMarks.set(key, svg);
  return svg;
}
